/**
 * Project Name: Brainsie eCommerce v4
 * Module: CMS Services
 * Description: API service functions for Content Management System
 * Author: Brainsie Team
 * Date Created: October 9, 2025
 */

import { postRecord, uploadRecord } from "./services";

// ============================================
// CMS Section Master APIs
// ============================================

/**
 * Get all section masters with pagination
 * @param {Object} paginationParams - { Page_Index, Records_Per_Page }
 * @returns {Promise} API response
 */
export const getSectionMasters = (paginationParams) => {
  return postRecord("cms/section-master/getall", paginationParams);
};

/**
 * Get section master by ID
 * @param {number} sectionMasterId - Section Master ID
 * @returns {Promise} API response
 */
export const getSectionMasterById = (sectionMasterId) => {
  return postRecord("cms/section-master/getbyid", {
    Section_Master_Id: sectionMasterId,
  });
};

/**
 * Create new section master
 * @param {Object} data - Section master data
 * @returns {Promise} API response
 */
export const createSectionMaster = (data) => {
  return postRecord("cms/section-master/create", data);
};

/**
 * Update existing section master
 * @param {Object} data - Section master data with Section_Master_Id
 * @returns {Promise} API response
 */
export const updateSectionMaster = (data) => {
  return postRecord("cms/section-master/update", data);
};

/**
 * Delete section master
 * @param {number} sectionMasterId - Section Master ID
 * @param {number} deletedBy - User ID who is deleting
 * @returns {Promise} API response
 */
export const deleteSectionMaster = (sectionMasterId, deletedBy) => {
  return postRecord("cms/section-master/delete", {
    Section_Master_Id: sectionMasterId,
    Deleted_By: deletedBy,
  });
};

// ============================================
// CMS Component Master APIs
// ============================================

/**
 * Get all component masters with pagination
 * @param {Object} paginationParams - { Page_Index, Records_Per_Page }
 * @returns {Promise} API response
 */
export const getComponentMasters = (paginationParams) => {
  return postRecord("cms/component-master/getall", paginationParams);
};

/**
 * Get component master by ID
 * @param {number} componentMasterId - Component Master ID
 * @returns {Promise} API response
 */
export const getComponentMasterById = (componentMasterId) => {
  return postRecord("cms/component-master/getbyid", {
    Component_Master_Id: componentMasterId,
  });
};

/**
 * Create new component master
 * @param {Object} data - Component master data
 * @returns {Promise} API response
 */
export const createComponentMaster = (data) => {
  return postRecord("cms/component-master/create", data);
};

/**
 * Update existing component master
 * @param {Object} data - Component master data with Component_Master_Id
 * @returns {Promise} API response
 */
export const updateComponentMaster = (data) => {
  return postRecord("cms/component-master/update", data);
};

/**
 * Delete component master
 * @param {number} componentMasterId - Component Master ID
 * @param {number} deletedBy - User ID who is deleting
 * @returns {Promise} API response
 */
export const deleteComponentMaster = (componentMasterId, deletedBy) => {
  return postRecord("cms/component-master/delete", {
    Component_Master_Id: componentMasterId,
    Deleted_By: deletedBy,
  });
};

// ============================================
// CMS Sections APIs (Page-specific instances)
// ============================================

/**
 * Get all CMS sections with pagination
 * @param {Object} paginationParams - { Page_Index, Records_Per_Page }
 * @returns {Promise} API response
 */
export const getCMSSections = (paginationParams) => {
  return postRecord("cms/sections/getall", paginationParams);
};

/**
 * Get CMS sections by page ID
 * @param {number} pageId - Page ID
 * @returns {Promise} API response
 */
export const getCMSSectionsByPage = (pageId) => {
  return postRecord("cms/sections/getbypage", {
    Page_Id: pageId,
  });
};

/**
 * Get CMS section by ID
 * @param {number} sectionId - Section ID
 * @returns {Promise} API response
 */
export const getCMSSectionById = (sectionId) => {
  return postRecord("cms/sections/getbyid", {
    Section_Id: sectionId,
  });
};

/**
 * Create new CMS section (with file upload support)
 * @param {FormData} formData - Form data including files
 * @returns {Promise} API response
 */
export const createCMSSection = (formData) => {
  return uploadRecord("cms/sections/create", formData);
};

/**
 * Update existing CMS section (with file upload support)
 * @param {FormData} formData - Form data including Section_Id and files
 * @returns {Promise} API response
 */
export const updateCMSSection = (formData) => {
  return uploadRecord("cms/sections/update", formData);
};

/**
 * Delete CMS section
 * @param {number} sectionId - Section ID
 * @param {number} deletedBy - User ID who is deleting
 * @returns {Promise} API response
 */
export const deleteCMSSection = (sectionId, deletedBy) => {
  return postRecord("cms/sections/delete", {
    Section_Id: sectionId,
    Deleted_By: deletedBy,
  });
};

// ============================================
// CMS Section Components APIs
// ============================================

/**
 * Get all section components with pagination
 * @param {Object} paginationParams - { Page_Index, Records_Per_Page }
 * @returns {Promise} API response
 */
export const getSectionComponents = (paginationParams) => {
  return postRecord("cms/section-components/getall", paginationParams);
};

/**
 * Get section components by section ID
 * @param {number} sectionId - Section ID
 * @returns {Promise} API response
 */
export const getSectionComponentsBySection = (sectionId) => {
  return postRecord("cms/section-components/getbysection", {
    Section_Id: sectionId,
  });
};

/**
 * Get section component by ID
 * @param {number} componentId - Component ID
 * @returns {Promise} API response
 */
export const getSectionComponentById = (componentId) => {
  return postRecord("cms/section-components/getbyid", {
    Component_Id: componentId,
  });
};

/**
 * Create new section component (with multiple file upload support)
 * @param {FormData} formData - Form data including multiple files
 * @returns {Promise} API response
 */
export const createSectionComponent = (formData) => {
  return uploadRecord("cms/section-components/create", formData);
};

/**
 * Update existing section component (with multiple file upload support)
 * @param {FormData} formData - Form data including Component_Id and files
 * @returns {Promise} API response
 */
export const updateSectionComponent = (formData) => {
  return uploadRecord("cms/section-components/update", formData);
};

/**
 * Delete section component
 * @param {number} componentId - Component ID
 * @param {number} deletedBy - User ID who is deleting
 * @returns {Promise} API response
 */
export const deleteSectionComponent = (componentId, deletedBy) => {
  return postRecord("cms/section-components/delete", {
    Component_Id: componentId,
    Deleted_By: deletedBy,
  });
};

/**
 * Get section components by section ID
 * @param {number} sectionId - Section ID
 * @returns {Promise} API response
 */
export const getSectionComponentsBySectionId = (sectionId) => {
  return postRecord("cms/section-components/getbysection", {
    Section_Id: sectionId,
  });
};
